package de.xtl.retroguide.question.scenes

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.StartOffset
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import de.xtl.retroguide.protocol.AnswerCategory
import de.xtl.retroguide.question.accentColor
import de.xtl.retroguide.question.label

/** Four tiles (4L, KALM, DAKI): emoji, label and count over a fill level that rises with the share of answers. */
@Composable
internal fun QuadrantScene(
    categories: List<AnswerCategory>,
    counts: Map<AnswerCategory, Int>,
    modifier: Modifier = Modifier,
) {
    val total = categories.sumOf { counts[it] ?: 0 }.coerceAtLeast(1)
    Column(
        modifier.fillMaxWidth().aspectRatio(4f / 3f),
        verticalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        categories.chunked(2).forEachIndexed { row, pair ->
            Row(Modifier.weight(1f), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                pair.forEachIndexed { column, category ->
                    QuadrantTile(
                        category = category,
                        count = counts[category] ?: 0,
                        share = (counts[category] ?: 0).toFloat() / total,
                        index = row * 2 + column,
                        modifier = Modifier.weight(1f).fillMaxHeight(),
                    )
                }
            }
        }
    }
}

@Composable
private fun QuadrantTile(
    category: AnswerCategory,
    count: Int,
    share: Float,
    index: Int,
    modifier: Modifier,
) {
    val fill = remember { Animatable(0f) }
    LaunchedEffect(share) { fill.animateTo(share, tween(900, delayMillis = index * 120, easing = FastOutSlowInEasing)) }
    val pulse by rememberInfiniteTransition().animateFloat(
        initialValue = 1f,
        targetValue = 1.12f,
        animationSpec = infiniteRepeatable(
            tween(900, easing = FastOutSlowInEasing),
            RepeatMode.Reverse,
            initialStartOffset = StartOffset(index * 300),
        ),
    )
    Box(modifier.clip(RoundedCornerShape(20.dp)).background(category.accentColor.copy(alpha = .12f))) {
        Box(
            Modifier
                .align(Alignment.BottomCenter)
                .fillMaxWidth()
                .fillMaxHeight(fill.value.coerceIn(0f, 1f))
                .background(category.accentColor.copy(alpha = .3f)),
        )
        Column(
            Modifier.fillMaxSize().padding(12.dp),
            verticalArrangement = Arrangement.SpaceBetween,
        ) {
            Text(
                category.emoji,
                fontSize = 28.sp,
                modifier = Modifier.graphicsLayer {
                    scaleX = pulse
                    scaleY = pulse
                },
            )
            Row(verticalAlignment = Alignment.Bottom) {
                Text(
                    category.label(),
                    modifier = Modifier.weight(1f),
                    style = MaterialTheme.typography.titleMedium,
                    color = category.accentColor,
                )
                Text(
                    count.toString(),
                    style = MaterialTheme.typography.headlineSmall,
                    fontWeight = FontWeight.Bold,
                    color = category.accentColor,
                )
            }
        }
    }
}
